import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
  Divider,
  Grid2,
  Collapse,
  Alert,
  Avatar,
  Slide
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HandymanIcon from '@mui/icons-material/Handyman';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import RecommendIcon from '@mui/icons-material/Recommend';
import PhotoIcon from '@mui/icons-material/Photo';
import { useHomeRepairsContext } from '../../contexts/HomeRepairsContext';
import { useHomeAttachmentsContext } from '../../contexts/HomeAttachmentsContext';
import { RepairItem } from '../../types/repairs';
import ImageSlider from '../ImageSlider';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface RepairCostEstimationModalProps {
  open: boolean;
  onClose: () => void;
}

const RepairCostEstimationModal: React.FC<RepairCostEstimationModalProps> = ({ 
  open, 
  onClose 
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('all');
  const { getAttachmentById, getAttachmentUrl } = useHomeAttachmentsContext();
  
  const { 
    repairs, 
    isLoading, 
    error,
    getRepairsSummary,
    toggleRepairResolution
  } = useHomeRepairsContext();

  // Get centralized calculations from context
  const { 
    costRange,
    averageRepairCost
  } = getRepairsSummary();

  // Use centralized calculations instead of duplicating logic
  const totalCost = averageRepairCost;
  const totalMin = costRange.min;
  const totalMax = costRange.max;

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getUrgencyIcon = (urgency: string) => {
    switch (urgency) {
      case 'high': return <WarningIcon fontSize="small" />;
      case 'medium': return <InfoIcon fontSize="small" />;
      case 'low': return <CheckCircleIcon fontSize="small" />;
      default: return <InfoIcon fontSize="small" />;
    }
  };

  /*
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'electrical': return '⚡';
      case 'plumbing': return '🔧';
      case 'hvac': return '🌡️';
      case 'structural': return '🏗️';
      case 'cosmetic': return '🎨';
      case 'exterior': return '🏠';
      default: return '🔨';
    }
  };
  */

  const formatTimeframe = (timeframe: string) => {
    const timeframes: { [key: string]: string } = {
      'immediate': 'ASAP',
      'within_month': 'This Month',
      'within_3_months': '3 Months',
      'within_6_months': '6 Months',
      'within_year': 'This Year'
    };
    return timeframes[timeframe] || timeframe;
  };

  const handleContactClick = (type: 'phone' | 'email', value: string) => {
    if (type === 'phone') {
      window.open(`tel:${value}`);
    } else if (type === 'email') {
      window.open(`mailto:${value}`);
    }
  };

  const handleWebsiteClick = (website: string) => {
    const url = website.startsWith('http') ? website : `https://${website}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Get images for a specific repair
  const getRepairImages = (repair: RepairItem): string[] => {
    if (!repair.related_image_ids || repair.related_image_ids.length === 0) {
      return [];
    }

    return repair.related_image_ids
      .map(imageId => {
        const attachment = getAttachmentById(imageId);
        return attachment ? getAttachmentUrl(attachment) : null;
      })
      .filter((url): url is string => url !== null);
  };

  // Convert timeframe_months to timeframe string for filtering
  const getTimeframeString = (months: number): string => {
    if (months === 0) return 'immediate';
    if (months <= 1) return 'within_month';
    if (months <= 3) return 'within_3_months';
    if (months <= 6) return 'within_6_months';
    return 'within_year';
  };

  // Update filteredRepairs to handle new field names and DIY filtering
  const filteredRepairs = selectedTimeframe === 'all' 
    ? repairs 
    : selectedTimeframe === 'immediate'
    ? repairs.filter(repair => repair.urgency === 'high')
    : selectedTimeframe === 'diy_friendly'
    ? repairs.filter(repair => repair.diy_friendly)
    : repairs.filter(repair => getTimeframeString(repair.timeframe_months) === selectedTimeframe);

  const urgentRepairs = repairs.filter(repair => repair.urgency === 'high');
  // const diyRepairs = repairs.filter(repair => repair.diy_friendly);

  // Handle loading state
  if (isLoading) {
    return (
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        sx={{ zIndex: 2100 }}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
      >
        <AppBar position="static" color="inherit" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Repair Cost Analysis
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography>Loading repairs...</Typography>
        </Box>
      </Dialog>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        sx={{ zIndex: 2100 }}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
      >
        <AppBar position="static" color="inherit" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Repair Cost Analysis
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2
        }}>
          <Alert severity="error">
            Error loading repairs: {error}
          </Alert>
        </Box>
      </Dialog>
    );
  }

  // Handle empty state
  if (repairs.length === 0) {
    return (
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        sx={{ zIndex: 2100 }}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={false}
      >
        <AppBar position="static" color="inherit" elevation={1}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Repair Cost Analysis
            </Typography>
            <IconButton edge="end" color="inherit" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 3
        }}>
          <Box sx={{ 
            textAlign: 'center',
            maxWidth: 400
          }}>
            <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Repairs Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Start a conversation with our AI assistant to analyze your home photos and identify potential repair items.
            </Typography>
            <Alert severity="info">
              Upload photos and chat with the AI to get started with your home analysis.
            </Alert>
          </Box>
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      sx={{ zIndex: 2100 }}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
    >
      <AppBar position="static" color="inherit" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Repair Cost Analysis
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Summary Cards - Mobile optimized */}
        <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
          <Grid2 container spacing={1}>
            <Grid2 size={6}>
              <Card sx={{ 
                textAlign: 'center', 
                backgroundColor: 'warning.light', 
                color: 'warning.contrastText',
                minHeight: 80
              }}>
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    ${totalCost.toLocaleString()}
                  </Typography>
                  <Typography variant="caption">
                    Estimated Total
                  </Typography>
                </CardContent>
              </Card>
            </Grid2>
            <Grid2 size={6}>
              <Card sx={{ 
                textAlign: 'center', 
                backgroundColor: 'error.light', 
                color: 'error.contrastText',
                minHeight: 80,
                cursor: 'pointer'
              }}
              onClick={() => setSelectedTimeframe('immediate')}
              >
                <CardContent sx={{ py: 1 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {urgentRepairs.length}
                  </Typography>
                  <Typography variant="caption">
                    Urgent Items
                  </Typography>
                </CardContent>
              </Card>
            </Grid2>
          </Grid2>

          {/* Cost Range */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Range: ${totalMin.toLocaleString()} - ${totalMax.toLocaleString()}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={75} 
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </Box>

        {/* Filter Buttons - Mobile optimized */}
        <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {['all', 'immediate', 'diy_friendly', 'within_month'].map((timeframe) => (
            <Button
              key={timeframe}
              variant={selectedTimeframe === timeframe ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSelectedTimeframe(timeframe)}
              sx={{ borderRadius: '16px', textTransform: 'none' }}
            >
              {timeframe === 'all' ? 'All' : 
               timeframe === 'immediate' ? 'Urgent' :
               timeframe === 'diy_friendly' ? 'DIY' :
               formatTimeframe(timeframe)}
            </Button>
          ))}
        </Box>

        {/* Repair Items List - Mobile optimized */}
        <Box sx={{ px: 2, pb: 2 }}>
          {filteredRepairs.map((repair) => {
            const repairImages = getRepairImages(repair);
            
            return (
              <Card key={repair.id} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                <CardContent sx={{ p: 2 }}>
                  {/* Header Row */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    mb: 1 
                  }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {repair.item}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        {repair.diy_friendly && (
                          <HandymanIcon color="success" fontSize="small" />
                        )}
                        {repair.referrals && repair.referrals.length > 0 && (
                          <RecommendIcon color="primary" fontSize="small" />
                        )}
                        {repairImages.length > 0 && (
                          <PhotoIcon color="secondary" fontSize="small" />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${Math.round((repair.cost_range[0] + repair.cost_range[1]) / 2).toLocaleString()}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => toggleExpanded(repair.id)}
                      >
                        {expandedItems.has(repair.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Chips Row - Mobile optimized */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Resolution Status Chip */}
                    <Chip 
                      icon={repair.is_resolved ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                      label={repair.is_resolved ? 'RESOLVED' : 'UNRESOLVED'} 
                      color={repair.is_resolved ? 'success' : 'default'}
                      variant={repair.is_resolved ? 'filled' : 'outlined'}
                      size="small"
                      clickable
                      onClick={() => toggleRepairResolution(repair.id)}
                    />
                    <Chip 
                      icon={getUrgencyIcon(repair.urgency)}
                      label={repair.urgency.toUpperCase()} 
                      color={getUrgencyColor(repair.urgency) as any}
                      size="small"
                    />
                    <Chip 
                      label={repair.category} 
                      variant="outlined" 
                      size="small"
                    />
                    {repair.area_name && (
                      <Chip 
                        label={repair.area_name} 
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>

                  {/* Description */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {repair.description}
                  </Typography>

                  {/* Related Images Preview (when collapsed) */}
                  {!expandedItems.has(repair.id) && repairImages.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <PhotoIcon fontSize="small" />
                        {repairImages.length} image{repairImages.length > 1 ? 's' : ''}
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        overflowX: 'auto',
                        pb: 1
                      }}>
                        {repairImages.slice(0, 3).map((imageUrl, index) => (
                          <Box
                            key={index}
                            sx={{
                              minWidth: 60,
                              height: 60,
                              borderRadius: 1,
                              overflow: 'hidden',
                              flexShrink: 0,
                              border: 1,
                              borderColor: 'divider'
                            }}
                          >
                            <img
                              src={imageUrl}
                              alt={`Issue evidence ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                          </Box>
                        ))}
                        {repairImages.length > 3 && (
                          <Box
                            sx={{
                              minWidth: 60,
                              height: 60,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.100',
                              border: 1,
                              borderColor: 'divider'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              +{repairImages.length - 3}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Expanded Details */}
                  <Collapse in={expandedItems.has(repair.id)}>
                    <Divider sx={{ my: 1 }} />
                    
                    {/* Cost and urgency details */}
                    <Grid2 container spacing={2} sx={{ mb: 2 }}>
                      <Grid2 size={6}>
                        <Typography variant="caption" color="text.secondary">
                          Cost Range
                        </Typography>
                        <Typography variant="body2">
                          ${repair.cost_range[0].toLocaleString()} - ${repair.cost_range[1].toLocaleString()}
                        </Typography>
                      </Grid2>
                      <Grid2 size={6}>
                        <Typography variant="caption" color="text.secondary">
                          Timeframe
                        </Typography>
                        <Typography variant="body2">
                          {formatTimeframe(getTimeframeString(repair.timeframe_months))}
                        </Typography>
                      </Grid2>
                    </Grid2>

                    {/* Related Images Slider (when expanded) */}
                    {repairImages.length > 0 && (
                      <>
                        <Typography variant="subtitle2" color="secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhotoIcon fontSize="small" />
                          Issue Evidence ({repairImages.length})
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <ImageSlider
                            images={repairImages}
                            height={120}
                          />
                        </Box>
                      </>
                    )}

                    {/* Referrals Section - Mobile optimized */}
                    {repair.referrals && repair.referrals.length > 0 && (
                      <>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RecommendIcon fontSize="small" />
                          Recommended Professionals
                        </Typography>
                        
                        {repair.referrals.map((referral) => (
                          <Card key={referral.id} sx={{ mb: 1, backgroundColor: 'background.default', border: 1, borderColor: 'primary.light' }}>
                            <CardContent sx={{ p: 1.5 }}>
                              {/* Referral Info Row */}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                                <Avatar 
                                  src={referral.profileImage} 
                                  sx={{ 
                                    bgcolor: 'primary.main',
                                    width: 40,
                                    height: 40
                                  }}
                                >
                                  {referral.name.charAt(0)}
                                </Avatar>
                                
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {referral.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {referral.company}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Specialties - Full width under referral info */}
                              {referral.specialties && referral.specialties.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                                  {referral.specialties.slice(0, 2).map((specialty, index) => (
                                    <Chip 
                                      key={index}
                                      label={specialty} 
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                    />
                                  ))}
                                  {referral.specialties.length > 2 && (
                                    <Typography variant="caption" color="text.secondary">
                                      +{referral.specialties.length - 2} more
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                              {/* Contact Actions - Full width under everything */}
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', overflowX: 'auto' }}>
                                {referral.phone && (
                                  <Button
                                    size="small"
                                    startIcon={<PhoneIcon />}
                                    onClick={() => handleContactClick('phone', referral.phone!)}
                                    sx={{ 
                                      textTransform: 'none', 
                                      fontSize: '0.7rem',
                                      minWidth: 'auto',
                                      px: 1,
                                      whiteSpace: 'nowrap',
                                      flex: 1
                                    }}
                                  >
                                    Call
                                  </Button>
                                )}
                                {referral.email && (
                                  <Button
                                    size="small"
                                    startIcon={<EmailIcon />}
                                    onClick={() => handleContactClick('email', referral.email!)}
                                    sx={{ 
                                      textTransform: 'none', 
                                      fontSize: '0.7rem',
                                      minWidth: 'auto',
                                      px: 1,
                                      whiteSpace: 'nowrap',
                                      flex: 1
                                    }}
                                  >
                                    Email
                                  </Button>
                                )}
                                {referral.website && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleWebsiteClick(referral.website!)}
                                    sx={{ 
                                      textTransform: 'none', 
                                      fontSize: '0.7rem',
                                      minWidth: 'auto',
                                      px: 1,
                                      whiteSpace: 'nowrap',
                                      flex: 1
                                    }}
                                  >
                                    Website
                                  </Button>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
          <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
            <Typography variant="caption">
              <strong>AI-Generated Estimates:</strong> These costs are estimates based on image analysis. Get professional quotes for accurate pricing.
            </Typography>
          </Alert>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RepairCostEstimationModal;